import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import tgcTerminalSvg from "../assets/tgcterminal.svg";
import thatsGirlCodeSvg from "../assets/thatsgirlcode.svg";
import {
  TERMINAL_THEMES,
  TerminalThemeName,
  TerminalTheme,
} from "./terminalThemes";

interface Props {
  theme?: TerminalThemeName;
  tagline: string;
  ctaLabel: string;
  title?: string;
}

export function Terminal({
  theme = "cream",
  tagline,
  ctaLabel,
  title = "~/thatsgirlcode/README.md",
}: Props) {
  const t = TERMINAL_THEMES[theme];
  const navigate = useNavigate();

  const fullCmd = "$ play --latest";
  const [cmd, setCmd] = useState("");
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      setCmd(fullCmd.slice(0, ++i));
      if (i >= fullCmd.length) clearInterval(id);
    }, 60);
    return () => clearInterval(id);
  }, []);

  const handlePlayClick = () => {
    navigate("/podcast#latest-player", { state: { autoplay: true } });
  };

  const badges = [
    { l: "podcast", v: "live", c: "#27c93f", pulse: true },
    { l: "episodes", v: "25", c: t.accent },
    { l: "build", v: "passing", c: "#27c93f" },
    { l: "vibes", v: "immaculate", c: t.accentSoft },
  ];

  return (
    <div
      className={clsx(
        "w-full overflow-hidden rounded-lg border font-mono shadow-2xl",
        t.frame,
      )}
    >
      {/* chrome */}
      <div
        className={clsx(
          "flex items-center gap-2 border-b px-3.5 py-2.5",
          t.chrome,
        )}
      >
        <span className="h-[11px] w-[11px] rounded-full bg-[#ff5f56]" />
        <span className="h-[11px] w-[11px] rounded-full bg-[#ffbd2e]" />
        <span className="h-[11px] w-[11px] rounded-full bg-[#27c93f]" />
        <span className={clsx("ml-4 text-[14px]", t.chromeText)}>{title}</span>
      </div>

      {/* body */}
      <div className="p-5 sm:p-8">
        <img
          src={thatsGirlCodeSvg}
          alt="That's Girl Code"
          className="block sm:hidden w-[75%] mx-auto -mt-14 -mb-10 mix-blend-multiply"
          style={{ background: "transparent" }}
        />
        <img
          src={tgcTerminalSvg}
          alt="That's Girl Code"
          className="hidden sm:block w-[95%] mx-auto -mt-16 -mb-8 mix-blend-multiply"
          style={{ background: "transparent" }}
        />

        <div className="mb-7 text-[15px] opacity-60">
          <Dot color={t.accent} />
          <span style={{ color: t.accentSoft }}>main</span>
          <span> · v2.1.1 · last commit 2 days ago</span>
        </div>

        {/* badges */}
        <div className="mb-7 flex flex-wrap gap-1.5">
          {badges.map((b) => (
            <span
              key={b.l}
              className="inline-flex overflow-hidden rounded-[3px] text-[13px]"
            >
              <span className="px-2 py-0.5" style={{ background: t.pillBg }}>
                {b.l}
              </span>
              <span
                className={clsx(
                  "px-2 py-0.5 inline-flex items-center gap-1",
                  b.pulse && "animate-pulse-soft",
                )}
                style={{ background: b.c, color: t.btnText }}
              >
                {b.pulse && (
                  <span
                    aria-hidden="true"
                    className="inline-block w-1.5 h-1.5 rounded-full bg-current"
                  />
                )}
                {b.v}
              </span>
            </span>
          ))}
        </div>

        {/* tagline */}
        <p className="mb-6 max-w-[520px] font-sans text-[18px] leading-relaxed opacity-90">
          {tagline}
        </p>

        {/* ## Quick start */}
        <div
          className="mb-3.5 mt-7 text-[16px]"
          style={{ color: t.accentSoft }}
        >
          <span className="opacity-50">## </span>
          <span className="font-sans font-semibold tracking-wide">
            Quick start
          </span>
        </div>

        {/* command box */}
        <div
          className="mb-3.5 border-l-2 px-4 py-3 text-[16px]"
          style={{ background: t.cmdBoxBg, borderLeftColor: t.cmdBoxBorder }}
        >
          <span style={{ color: t.accentSoft }}>{cmd}</span>
          <span
            className="ml-0.5 inline-block h-4 w-2.5 align-middle animate-blink"
            style={{ background: t.accentSoft }}
          />
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2.5">
          <button
            onClick={handlePlayClick}
            className="inline-flex items-center justify-center sm:justify-start gap-2 px-4 py-3 text-[15px] tracking-wide"
            style={{ background: t.btnBg, color: t.btnText }}
          >
            ▶ run<span className="opacity-60">·</span>
            {ctaLabel.toLowerCase()}
          </button>

          <div className="flex flex-row items-stretch sm:items-center gap-2.5">
            <PlatformLink
              icon="♫"
              iconColor="#1ED760"
              label="spotify"
              href="https://open.spotify.com/show/6eDM2coAkTGam11bhZts0G"
              theme={t}
            />
            <PlatformLink
              icon="◐"
              iconColor={t.accentSoft}
              label="apple"
              href="https://podcasts.apple.com/us/podcast/thats-girl-code/id1826327958"
              theme={t}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Dot({ color }: { color: string }) {
  return (
    <span
      className="mr-2 inline-block h-2 w-2 rounded-full align-middle"
      style={{ background: color }}
    />
  );
}

function PlatformLink({
  icon,
  iconColor,
  label,
  href,
  theme,
}: {
  icon: string;
  iconColor: string;
  label: string;
  href: string;
  theme: TerminalTheme;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 border px-4 py-3 text-[15px] no-underline"
      style={{ borderColor: theme.accentSoft, opacity: 0.95 }}
    >
      <span style={{ color: iconColor }}>{icon}</span>
      {label}
    </a>
  );
}
